import { PlayButton } from '@components/Track/PlayButton'
import { colors } from '@components/shared'
import { OutlineOnFocusVisible } from '@components/shared/buttons'
import { GoodSansB4, GoodSansH1, GoodSansH3, GoodSansB5 } from '@components/shared/typography'
import { useRouter } from 'next/router'
import * as React from 'react'
import { config } from 'react-awesome-styled-grid'
import styled from 'styled-components'
import type { TrackPropsType } from '../../models/Track'

export function TrackPreview ({
  track,
  slug,
  title,
  description,
  popularity,
  imageUrl,
  isCoverflow,
  isSmall
}: {
  track: TrackPropsType,
  slug: string,
  title: string,
  description: string,
  popularity: number,
  imageUrl: string,
  isCoverflow?: boolean,
  isSmall?: boolean
}): React.ReactNode {
  const router = useRouter()
  const click = React.useCallback((): void => {
    router.push(slug)
  }, [slug])

  return (
    <OutlineWrapper>
      <Container
        onClick={click}
        role="button"
        tabIndex={0}
        alt={`Open ${title}`}
      >
        <img src={imageUrl} alt={title} />
        <Gradient />
        <Content isCoverflow={isCoverflow}>
          <div>
            <Header isSmall={isSmall}>{title}</Header>
            <CopyContainer>
              <Copy isSmall={isSmall}>
                {description}
              </Copy>
              {popularity > 0
                ? <TotalPlays isSmall={isSmall}>{`${popularity.toLocaleString()} plays`}</TotalPlays>
                : ''}
            </CopyContainer>
          </div>
          <PreviewContainer>
            <PlayButton {...track} size={isSmall ? 28 : 32} isIconStyle />
          </PreviewContainer>
        </Content>
      </Container>
    </OutlineWrapper>
  )
}

const Header = styled.p<any>`
  ${(props: any): string => props.isSmall ? GoodSansH3 : GoodSansH1};
`
const CopyContainer = styled.div<any>`
  display: flex;
  flex-wrap: wrap;
`
const Copy = styled.p<any>`
  ${(props: any): string => props.isSmall ? GoodSansB5 : GoodSansB4};
  margin-right: 1em;
  display: inline;
  margin-top: 3px;
`
const TotalPlays = styled.p<any>`
  ${(props: any): string => props.isSmall ? GoodSansB5 : GoodSansB4};
  margin-top: 3px;
  position: relative;
  :before {
    content: "|";
    position: absolute;
    left: -.7em;
  }
`
const Gradient = styled.div<any>`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 50%;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.7) 100%);
`
const Content = styled.div<any>`
  align-items: flex-end;
  display: flex;
  justify-content: space-between;
  position: absolute;
  overflow: hidden;
  bottom: 20px;
  left: 18px;
  right: 18px;
  ${(props: any): any => config(props).media.md`
    left: ${(props: any): string => props.isCoverflow ? '20px' : '28px'};
    right: ${(props: any): string => props.isCoverflow ? '20px' : '28px'};
    bottom: ${(props: any): string => props.isCoverflow ? '20px' : '30px'};
  `}
`
const OutlineWrapper = styled.div<any>`
  padding: 2px; /* space for the accessible outline when the Container has keyboard focus */
`
const Container = styled.div<any>`
  background-color: ${colors.midnightBlueTint};
  border-radius: 30px;
  cursor: pointer;
  margin: 0 0 24px;
  padding-top: 100%;
  position: relative;
  width: 100%;
  overflow: hidden;
 ${OutlineOnFocusVisible};
  img {
    bottom: 0;
    border-radius: 30px;
    height: 100%;
    left: 0;
    position: absolute;
    right: 0;
    top: 0;
    width: 100%;
  }
`

const PreviewContainer = styled.div<any>`
  margin: 0 0 0 12px;
  padding-top: 2px; /* needed for accessible focus */
`
